// ============================================================
// Sidebar.tsx
// Global persistent sidebar — wraps every authenticated screen.
// Two responsive modes:
//   - Desktop (md+): fixed-position flex sibling that takes 240px
//     when expanded or 60px in rail mode. Chevron toggles between.
//   - Mobile (<md): slide-in overlay over the top of the page, with
//     a black/30 backdrop. Hamburger in AppHeader opens it; backdrop
//     click / Escape / internal close-X dismisses it.
//
// Layout (top to bottom):
//   1. Top bar  — chevron (desktop) / close X (mobile)
//   2. Nav block (flex-1, overflow-y-auto):
//        Cases    ← standalone button, no section heading. Routes
//                   to CaseListScreen (clears currentCase + closes
//                   all takeovers). Active-state highlight when
//                   CaseListScreen is what's rendering.
//        GENERAL header
//          - Client Search
//          - Calendar
//          - Tasks
//          - Document Search
//        RECENT CASES header
//          - Recent cases (last 24h, by lastAccessedAt desc)
//   3. Account section (border-t):
//        - Settings   ← functional
//        - Sign Out   ← functional
//
// In rail mode the GENERAL placeholders show as icon-only. FAVOURITES and
// RECENT CASES (no room for names in a 64px rail) are ONE icon each — a
// star / a history clock with a count badge — that opens a named flyout
// (SidebarFlyout: hover after a short delay, click, or the keyboard).
//
// State lives on useCasevoStore so AppHeader's hamburger and this
// component can communicate without lifting state through App.tsx.
// sidebarCollapsed now persists to the backend User row via
// viewingPaneToggles['sidebar.collapsed'] (usePersistedToggle,
// debounced updateMeApi save); sidebarMobileOpen resets to false
// on every page load; recentCases is loaded on mount and refreshed
// each time currentCase changes.
// ============================================================
import React, { useEffect, useRef, useState } from 'react'
import { useCasevoStore } from '../../store/useCasevoStore'
import { useFirmChange } from '../../utils/liveEvents'
import { useLivePermissions } from '../../hooks/useLivePermissions'
import { navigateTo } from '../../navigation/navigate'
import { usePersistedToggle } from '../../hooks/usePersistedToggle'
import RailTooltip from '../shared/RailTooltip'
import ConfirmDialogue from '../shared/ConfirmDialogue'
import SupportModal from '../shared/SupportModal'
import FavouriteStar from '../shared/FavouriteStar'
import CaseActionsMenu, { type CaseActionsMenuState } from '../shared/CaseActionsMenu'
import type { CaseSummary } from './CaseListScreen'
import type { RawCase } from '../../types/case.types'
import type { Favourite } from '../../api/casevoApi'
import { fetchCaseById, caseOpenErrorMessage } from '../../api/casevoApi'
import CaseQuickViewModal from '../shared/CaseQuickViewModal'
import { rawToSummary } from '../../utils/caseSummary'
import { caseName, caseRef, type CaseNameSource } from '../../utils/caseName'
import SidebarFlyout, { type FlyoutItem } from './SidebarFlyout'

// ── ICON SUBCOMPONENTS ─────────────────────────────────────
// Project pattern is inline SVGs (no icon library) — these mirror
// the lucide-react glyphs the brief calls for. Each uses
// currentColor + 20×20 viewBox so the rail/expanded states keep
// pixel-consistent sizing.
const iconBase = 'h-5 w-5 shrink-0'
const SearchIcon: React.FC = () => (
  <svg className={iconBase} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
)
const CalendarIcon: React.FC = () => (
  <svg className={iconBase} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
)
const CheckSquareIcon: React.FC = () => (
  <svg className={iconBase} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="9 11 12 14 22 4" />
    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
  </svg>
)
const FileSearchIcon: React.FC = () => (
  <svg className={iconBase} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <circle cx="11.5" cy="14.5" r="2.5" />
    <line x1="13.25" y1="16.25" x2="15" y2="18" />
  </svg>
)
// Invoicing Stage 1 — Billing (receipt).
const BillIcon: React.FC = () => (
  <svg className={iconBase} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 2v20l3-2 3 2 3-2 3 2 3-2 1 .7V2l-1 .7-3-2-3 2-3-2-3 2-3-2z" />
    <path d="M8 8h8M8 12h8M8 16h4" />
  </svg>
)
const SettingsIcon: React.FC = () => (
  <svg className={iconBase} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
)
const LogOutIcon: React.FC = () => (
  <svg className={iconBase} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
)
// Chat-bubble glyph for the account-section "Support" item. Lucide's
// canonical MessageCircle outline — a rounded speech bubble with the
// small tail at the bottom-left. A chat bubble reads as "contact us"
// (this opens a message form) rather than the "help docs" a question
// mark implies. Same currentColor stroke weight and iconBase sizing
// as the rest of the sidebar icon family so it reads as a native row.
const MessageCircleIcon: React.FC = () => (
  <svg className={iconBase} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
  </svg>
)
// Shield glyph for the admin-only "Admin" nav item. Lucide's
// canonical shield outline — currentColor stroke matches the
// rest of the sidebar icon family.
const ShieldIcon: React.FC = () => (
  <svg className={iconBase} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
)
// Folder glyph for the top-of-sidebar "Cases" button. Lucide's
// canonical Folder shape — single-path outline with a small tab
// notch on the top-left edge. Cleaner silhouette than briefcase
// at small sizes; same currentColor stroke weight as the rest of
// the sidebar icons.
const FolderIcon: React.FC = () => (
  <svg className={iconBase} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
  </svg>
)
// ★ ACTIVE ITEM — Option B (casevo-sidebar-gold-highlight mockup): Admin's
// raised section-nav card translated to the forest sidebar, gold kept to the
// 3px inset BAR + the ICON (no gold fill). Exact mockup values:
//   card   bg rgba(255,255,255,.065) · 1px border rgba(255,255,255,.09) ·
//          radius 10px · shadow 0 1px 2px rgba(0,0,0,.25),0 4px 14px rgba(0,0,0,.18)
//   text   #fff, semibold          icon  #e0b64a (casevo-gold)
//   bar    absolute left:-1px, top/bottom 7px (rail 8px), 3px wide (18px
//          tall — same length as before the 2px vertical inset),
//          radius 0 3px 3px 0, #e0b64a
// The card is inset 8px each side (mx-2, like the mockup's list padding) and
// 2px top/bottom (my-[2px]); its content padding is reduced by margin+border
// (16 → 8+1+7 horizontally, 8 → 2+1+5 vertically) so the icon / text never
// shift when a row becomes active, and the 36px row pitch is unchanged. The
// 2px vertical inset (shared with the hover card, ROW_CARD) leaves a 4px gap
// so an adjacent hover never touches the selected card.
// The sidebar is forest in BOTH themes (--casevo-header), so one treatment
// serves light + dark.
const ACTIVE_CARD =
  'relative mx-2 my-[2px] w-[calc(100%-16px)] rounded-[10px] border border-[rgba(255,255,255,0.09)] ' +
  'bg-[rgba(255,255,255,0.065)] shadow-[0_1px_2px_rgba(0,0,0,0.25),0_4px_14px_rgba(0,0,0,0.18)] ' +
  "before:content-[''] before:absolute before:left-[-1px] before:w-[3px] before:rounded-r-[3px] before:bg-[#e0b64a]"
const ACTIVE_BAR_ROW = 'before:top-[7px] before:bottom-[7px]'
// ★ HOVER uses the SAME footprint as the selected card (inset 8px each side,
// radius 10px, 1px border — transparent at rest) so hover previews the
// selected shape and an adjacent hover + selected keep an 8px-free gap
// instead of a full-width fill butting into the inset card. Subtler than
// ACTIVE_CARD: a faint fill + faint border, no gold bar, no shadow.
// Box maths = ACTIVE_CARD's, so nothing shifts: margin 8 + border 1 + padding
// 7 = the old 16px inset horizontally; vertically my-[2px] + 1px border +
// py-[5px] = the old py-2 (8px) → rows keep their 36px (52px two-line) pitch,
// but the VISIBLE card is 4px shorter, leaving a 4px gap between an adjacent
// hover and the selected card (they never touch). The row CONTAINERS are
// flex-col so these margins don't collapse between adjacent rows.
// Sidebar is forest in both themes (--casevo-header) → one value set.
const ROW_CARD = 'mx-2 my-[2px] w-[calc(100%-16px)] rounded-[10px] border border-transparent'
const ROW_HOVER = 'hover:bg-[rgba(255,255,255,0.04)] hover:border-[rgba(255,255,255,0.05)]'
const ROW_PRESS = 'active:bg-[rgba(255,255,255,0.08)]'
const ROW_FOCUS = 'focus-visible:bg-[rgba(255,255,255,0.04)] focus-visible:border-[rgba(255,255,255,0.35)]'

// Stage B: dashboard glyph (Lucide LayoutDashboard). 2x2 grid of
// asymmetric rectangles — the visual shorthand for a dashboard/home
// page across most design systems. Matches the rest of the sidebar
// icons in stroke weight + currentColor styling. Inline SVG (not
// lucide-react — the project uses hand-rolled SVGs).
const LayoutDashboardIcon: React.FC = () => (
  <svg className={iconBase} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="3" width="7" height="9" />
    <rect x="14" y="3" width="7" height="5" />
    <rect x="14" y="12" width="7" height="9" />
    <rect x="3" y="16" width="7" height="5" />
  </svg>
)
// X icon for the mobile close button.
const CloseIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
)

// ── HAMBURGER TRIGGER (exported) ──────────────────────────
// Lives in AppHeader's leftHamburger slot on each authenticated
// screen. md:hidden — desktop already shows the sidebar inline,
// so a trigger isn't needed there.
export const SidebarHamburger: React.FC = () => {
  const setSidebarMobileOpen = useCasevoStore((s) => s.setSidebarMobileOpen)
  return (
    <button
      type="button"
      onClick={() => setSidebarMobileOpen(true)}
      aria-label="Open menu"
      className="md:hidden text-casevo-light hover:text-white transition-colors p-2
                 focus:outline-none focus-visible:ring-2 focus-visible:ring-casevo-light rounded"
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <line x1="3" y1="6" x2="21" y2="6" />
        <line x1="3" y1="12" x2="21" y2="12" />
        <line x1="3" y1="18" x2="21" y2="18" />
      </svg>
    </button>
  )
}

// ── GENERAL NAV ITEMS ─────────────────────────────────────
// All four wired as main-area takeovers (Phase 2b–2f). Each
// button highlights via rowClass()'s `active` branch when its
// matching show* flag is true. The fifth Phase 1 placeholder,
// Recent Cases, graduated out of this list into its own dedicated
// RECENT CASES zone below.
const NAV_ITEMS_GENERAL: { label: string; Icon: React.FC }[] = [
  { label: 'Client Search', Icon: SearchIcon },
  { label: 'Calendar', Icon: CalendarIcon },
  // ★ "My work" — the Tasks zone, renamed with the execution-queue redesign.
  { label: 'My work', Icon: CheckSquareIcon },
  { label: 'Document Search', Icon: FileSearchIcon },
  // Invoicing Stage 1 — shown only to members with billing access (see filter below).
  { label: 'Billing', Icon: BillIcon },
]

// ── COMPACT RELATIVE-TIME HELPER ──────────────────────────
// utils/relativeTime emits "2 hours ago" / "23 minutes ago" — too
// long to fit alongside the 8-char case reference on a single line
// in the 240px sidebar. This compact variant follows the brief's
// brackets exactly: < 60s → "just now", < 60m → "Nm ago", < 24h →
// "Nh ago", >= 24h → "yesterday" (defensive — the 24h backend
// filter should preclude this branch in practice).
const compactRelativeTime = (input: string | Date): string => {
  const date = typeof input === 'string' ? new Date(input) : input
  const diffMs = Date.now() - date.getTime()
  if (diffMs < 0) return 'just now'
  const seconds = Math.floor(diffMs / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  return 'yesterday'
}

// ── DISPLAY-NAME HELPER ───────────────────────────────────
// Mirrors CaseListScreen's getDisplayName — prefers the live linked
// Client (Phase 4) and falls back to the case row's frozen snapshot
// fields. "New Case" for a not-yet-named case so the row reads as
// something rather than a blank.
// Subtle type indicator shown under each favourite's label in the
// FAVOURITES section (a case / client / task).
const FAVOURITE_TYPE_LABELS: Record<Favourite['itemType'], string> = {
  case: 'Case',
  client: 'Client',
  task: 'Task',
}
// Recent-case label — the shared caseName() rule (utils/caseName.ts).
const getRecentCaseDisplayName = (c: CaseNameSource): string => caseName(c)

// ── MAIN COMPONENT ─────────────────────────────────────────
// How many favourites / recent cases this device showed last time — the
// loading placeholders reserve that many rows so the real list doesn't push
// the sections below it (no layout shift). First visit → 1.
const FAV_COUNT_KEY = 'casevo_sidebar_fav_count'
const RECENT_COUNT_KEY = 'casevo_sidebar_recent_count'
const rememberedCount = (key: string): number => {
  try {
    const raw = window.localStorage.getItem(key)
    const n = raw === null ? 1 : Number(raw)
    return Number.isInteger(n) && n >= 0 ? Math.min(n, 12) : 1
  } catch { return 1 }
}
const rememberCount = (key: string, n: number): void => {
  try { window.localStorage.setItem(key, String(n)) } catch { /* storage blocked */ }
}

const Sidebar: React.FC = () => {
  // Sidebar-rail collapsed state, persisted to the authenticated
  // user's viewingPaneToggles['sidebar.collapsed'] via the debounced
  // updateMeApi save (see hooks/usePersistedToggle.ts + store's
  // scheduleTogglesSave). Replaces the previous localStorage
  // plumbing (`casevo_sidebar_collapsed`) so the preference follows
  // the user across devices instead of living in one browser's
  // storage. Default false (expanded) for pre-first-write users.
  // Read-only here — the collapse toggle now lives on the seam handle in
  // App.tsx (same store-backed key), so the Sidebar only reads the state.
  const [sidebarCollapsed, setSidebarCollapsed] = usePersistedToggle('sidebar.collapsed', false)
  const sidebarMobileOpen = useCasevoStore((s) => s.sidebarMobileOpen)
  const setSidebarMobileOpen = useCasevoStore((s) => s.setSidebarMobileOpen)
  // Per-key selectors — re-render only when the slice we care about
  // changes, not when an unrelated piece of the store updates.
  const isAuthenticated = useCasevoStore((s) => s.isAuthenticated)
  const currentCaseId = useCasevoStore((s) => s.currentCase?.id)
  // ★ LIVE favourite labels (same rule + sources as Recent Cases). A case
  // favourite's stored label was frozen at star-time; now it's re-derived with
  // the shared caseName()/caseRef() from the freshest data we hold:
  //   1. the OPEN case (its editable snapshot + defendant — updates as you type),
  //   2. the Recent Cases row for it (refreshed like Recent already is),
  //   3. otherwise the server label — which GET /api/favourites now re-derives
  //      live from the case row on every load (favouritesController).
  const liveCurrentLabel = useCasevoStore((s) => {
    const c = s.currentCase
    if (!c) return null
    return `${caseName({ caseName: c.caseName, clientForename: c.clientDetails.forename, clientSurname: c.clientDetails.surname, defendantName: c.defendant?.name })} · ${caseRef({ id: c.id, createdAt: c.createdAt, clientSurname: c.clientDetails.surname })}`
  })
  const favLabel = (fav: { itemType: string; itemId: string; label: string }): string => {
    if (fav.itemType !== 'case') return fav.label
    if (fav.itemId === currentCaseId && liveCurrentLabel) return liveCurrentLabel
    const recent = recentCasesForLabels?.find((c) => c.id === fav.itemId)
    if (recent) return `${caseName(recent)} · ${caseRef(recent)}`
    return fav.label
  }
  const recentCases = useCasevoStore((s) => s.recentCases)
  const recentCasesForLabels = recentCases
  const favourites = useCasevoStore((s) => s.favourites)
  useEffect(() => { if (favourites) rememberCount(FAV_COUNT_KEY, favourites.length) }, [favourites])
  useEffect(() => { if (recentCases) rememberCount(RECENT_COUNT_KEY, recentCases.length) }, [recentCases])
  // Drag-to-reorder state for the FAVOURITES list (expanded desktop only,
  // native HTML5 drag). favDragIndex = the row being dragged; favDragOver
  // Index = the row currently hovered (drop target, for the indicator).
  const [favDragIndex, setFavDragIndex] = useState<number | null>(null)
  const [favDragOverIndex, setFavDragOverIndex] = useState<number | null>(null)
  // Right-click case actions menu (Recent + Favourite case rows). Reuses the
  // same CaseActionsMenu controller the case list uses — full action set.
  const [caseMenu, setCaseMenu] = useState<CaseActionsMenuState | null>(null)
  // A recent row already has the full RawCase (superset of CaseSummary) — open
  // the menu instantly from it. Favourites carry only id+label, so fetch the
  // case by id first so every action has the data it needs.
  const openRecentCaseMenu = (e: React.MouseEvent, c: RawCase): void => {
    e.preventDefault()
    setCaseMenu({ x: e.clientX, y: e.clientY, target: c as unknown as CaseSummary })
  }
  const openFavouriteCaseMenu = (e: React.MouseEvent, fav: Favourite): void => {
    e.preventDefault()
    const x = e.clientX
    const y = e.clientY
    const caseId = fav.itemId
    // Show the menu INSTANTLY with a MINIMAL target (id is all the common
    // actions — open / new-tab / rename / delete / link / assign — actually
    // need; CSV/PDF export already fetch by id on click). Defaults keep the
    // data-hungry bits (status label, copy-ref, client-details) sane until the
    // background fetch below upgrades the target in place.
    const nowIso = new Date().toISOString()
    const minimal = {
      id: caseId,
      caseName: null,
      client: null,
      clientForename: '',
      clientSurname: '',
      closeDate: null,
      setupFinalised: false,
      createdAt: nowIso,
      updatedAt: nowIso,
      assignedToUserId: null,
    } as unknown as CaseSummary
    setCaseMenu({ x, y, target: minimal })
    // Upgrade to the FULL case in the background (non-blocking). When it lands,
    // swap in the real data — but only if this same favourite's menu is still
    // the one open (guard against a closed menu or a different right-click).
    void fetchCaseById(caseId)
      .then((full) => {
        setCaseMenu((cur) =>
          cur && cur.target.id === caseId
            ? { ...cur, target: full as unknown as CaseSummary }
            : cur,
        )
      })
      .catch(() => { /* dead / inaccessible case — leave the minimal menu */ })
  }
  // Drag gate: set true on the handle's mousedown, checked in onDragStart.
  // A ref (not state) because we read it synchronously inside dragstart and
  // don't want a re-render. Reset on dragEnd + a window mouseup (so a
  // mousedown that never became a drag doesn't leave it stuck true).
  const dragFromHandleRef = useRef(false)
  useEffect(() => {
    const clear = (): void => { dragFromHandleRef.current = false }
    window.addEventListener('mouseup', clear)
    return () => window.removeEventListener('mouseup', clear)
  }, [])
  // Reactive subscriptions for the 5 takeover flags. Used to derive
  // the Cases button's active-state indicator (active when no
  // takeover is open AND no case is selected — i.e. CaseListScreen
  // is what's actually rendering). Other handlers read these via
  // getState() inside their bodies, which doesn't trigger re-renders
  // — but the active-state visual needs to update reactively, hence
  // the selector subscriptions here.
  const showSettings = useCasevoStore((s) => s.showSettings)
  const showAdmin = useCasevoStore((s) => s.showAdmin)
  const showClientSearch = useCasevoStore((s) => s.showClientSearch)
  const showCalendar = useCasevoStore((s) => s.showCalendar)
  const showDocumentSearch = useCasevoStore((s) => s.showDocumentSearch)
  const showBilling = useCasevoStore((s) => s.showBilling)
  // Bills follow the colleagues'-£ rule (server-enforced); hide the nav item otherwise.
  // Billing is for everyone — each person sees the bills on cases they can access (server-scoped).
  const canUseBilling = useCasevoStore((s) => !!s.authUser)
  const showTasks = useCasevoStore((s) => s.showTasks)
  // Stage B: dashboard subscription for the Dashboard button's
  // active-highlight AND to un-highlight the Cases button when
  // Dashboard is the current view.
  const showDashboard = useCasevoStore((s) => s.showDashboard)
  // authUser is read here so the Admin nav item below can be
  // role-gated. Subscribe to the slice (not getState) so a
  // promote/demote of the current user re-renders the sidebar
  // and toggles the link visibility.
  const authUser = useCasevoStore((s) => s.authUser)
  // Local state for the Sign Out confirmation dialogue — keeps it tied
  // to this component's lifecycle rather than the global store.
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false)
  // Local state for the Support modal. Kept local (not a store
  // takeover flag) because it's a self-contained overlay, not a
  // main-area view — same lifecycle rationale as showSignOutConfirm.
  const [showSupport, setShowSupport] = useState(false)
  // Body scroll lock while the mobile overlay is open — restored on
  // close / unmount so the next render starts from a known state.
  useEffect(() => {
    if (sidebarMobileOpen) {
      const previous = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      return () => {
        document.body.style.overflow = previous
      }
    }
    return undefined
  }, [sidebarMobileOpen])
  // Escape closes the mobile overlay. No-op on desktop (the overlay
  // never appears there). Attached only while open to avoid global
  // listener churn.
  useEffect(() => {
    if (!sidebarMobileOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSidebarMobileOpen(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [sidebarMobileOpen, setSidebarMobileOpen])
  // Recent-cases loader — fires on mount when authenticated AND every
  // time currentCase changes. Opening a case bumps its lastAccessedAt
  // on the backend, so re-fetching pulls the just-opened case to the
  // top of the list. Guarded on isAuthenticated even though Sidebar
  // only renders authenticated — belt-and-braces in case future routing
  // changes the mount condition. Errors are swallowed inside the store
  // action; nothing to await here.
  useEffect(() => {
    if (!isAuthenticated) return
    void useCasevoStore.getState().loadRecentCases()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, currentCaseId])
  // (D12 live updates) Cases renamed / closed / deleted by someone else →
  // Recent + Favourites re-read.
  // A favourite client's "Restricted" label follows a live permission change.
  useLivePermissions(() => { const st = useCasevoStore.getState(); if (st.favourites) void st.loadFavourites() })
  useFirmChange(['cases', 'clients'], () => {
    const st = useCasevoStore.getState()
    void st.loadRecentCases()
    if (st.favourites) void st.loadFavourites()
  }, isAuthenticated)
  // Stage B: Dashboard nav handler. Same shape as every other
  // takeover handler — flush pending saves, close all takeovers,
  // flip own flag on, close mobile overlay. Dashboard is a takeover
  // like the rest (with the added twist of being the initial-value
  // default in the store).
  const handleDashboard = async () => {
    // navigateTo flushes pending autosaves + asks the unsaved-changes guard.
    if (!(await navigateTo({ view: 'dashboard' }))) return
    setSidebarMobileOpen(false)
  }
  // Top-of-sidebar "Cases" handler — routes the user to whatever
  // ViewingPane renders as its default, which is CaseListScreen
  // when no case is selected. Mirrors handleOpenRecentCase's
  // close-all-takeovers shape but substitutes clearCurrentCase()
  // for the per-case setCurrentCaseById call. Flushes auto-saves
  // first so any pending edits on the currently-open case (if any)
  // aren't lost in transit — symmetric with every other Sidebar
  // handler.
  const handleCases = async () => {
    if (!(await navigateTo({ view: 'caseList' }))) return
    setSidebarMobileOpen(false)
  }
  // Account-section "Settings" handler. Flushes the three auto-save
  // streams before navigating so the last second of debounced edits
  // (case data, linked-client edits, per-witness edits) isn't lost on
  // the way out. On CaseListScreen / Settings the flushes are no-ops
  // — safe to always call. Closes the mobile overlay so the user lands
  // on the destination with the sidebar dismissed.
  const handleSettings = async () => {
    if (!(await navigateTo({ view: 'settings' }))) return
    setSidebarMobileOpen(false)
  }
  // GENERAL-zone "Client Search" handler (Phase 2b). Same flush-then-
  // navigate pattern as Settings. App.tsx renders ClientSearchView
  // when showClientSearch is true (checked before showSettings so a
  // ClientSearch click from the Settings screen wins).
  const handleClientSearch = async () => {
    // Always the results (also when already inside the zone on a client).
    if (!(await navigateTo({ view: 'clientSearch' }))) return
    useCasevoStore.getState().bumpClientNav()
    setSidebarMobileOpen(false)
  }
  // GENERAL-zone "Calendar" handler (Phase 2c). Same flush-then-
  // navigate-with-mutual-exclusion pattern as Client Search.
  const handleCalendar = async () => {
    if (!(await navigateTo({ view: 'calendar' }))) return
    // Trigger load on open. Cached entries flash first; the fetch
    // replaces them when it lands.
    void useCasevoStore.getState().loadCalendarEntries()
    setSidebarMobileOpen(false)
  }
  // GENERAL-zone "Document Search" handler (Phase 2d). Same
  // flush-then-navigate-with-mutual-exclusion pattern.
  const handleDocumentSearch = async () => {
    if (!(await navigateTo({ view: 'documentSearch' }))) return
    setSidebarMobileOpen(false)
  }
  // Invoicing Stage 1 — firm Billing takeover (same flush-then-navigate pattern).
  const handleBilling = async () => {
    if (!(await navigateTo({ view: 'billing' }))) return
    setSidebarMobileOpen(false)
  }
  // GENERAL-zone "Tasks" handler (Phase 2f). Same flush-then-
  // navigate-with-mutual-exclusion pattern as the other takeovers.
  const handleTasks = async () => {
    if (!(await navigateTo({ view: 'tasks' }))) return
    setSidebarMobileOpen(false)
  }
  // ★ Recent-case / favourited-case click → the CASE QUICK-VIEW MODAL (the
  // same CaseQuickViewModal the case list + client case-chips open), not the
  // full page. Mirrors ClientSearchView's chip path: fetch the case
  // (getCaseById — access-checked server-side), narrow it with the shared
  // rawToSummary, seed the modal with the full row so it paints immediately.
  // "Full case →" in the modal opens the full page via openFullCase below.
  // A 403/404 surfaces the shared clean message (never a crash). One open at a
  // time (a double-click is ignored while the fetch is in flight).
  const [sidebarQuickView, setSidebarQuickView] = useState<{ summary: CaseSummary; full: RawCase } | null>(null)
  const openingQuickViewRef = useRef<string | null>(null)
  const openCaseQuickView = async (id: string, onMissing?: () => void): Promise<void> => {
    if (openingQuickViewRef.current) return
    openingQuickViewRef.current = id
    setSidebarMobileOpen(false)
    try {
      const raw = await fetchCaseById(id)
      setSidebarQuickView({ summary: rawToSummary(raw), full: raw })
    } catch (err) {
      const msg = caseOpenErrorMessage(err)
      useCasevoStore.getState().setSaveConflict(msg ?? 'Failed to open case. Please try again.')
      if (msg && onMissing) onMissing()
    } finally {
      openingQuickViewRef.current = null
    }
  }
  // "Full case →" from the modal — the sidebar's previous open-case path:
  // flush pending edits, drop any takeover (incl. Dashboard), open the case
  // with the modal's already-fetched row (no second getCaseById).
  const openFullCase = async (id: string, prefetched?: RawCase): Promise<void> => {
    const store = useCasevoStore.getState()
    try {
      await navigateTo({ view: 'caseDetail', caseId: id }, { prefetched, intents: (s) => s.setCaseOpenedFrom('caseList') })
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[Sidebar] Failed to open case:', err)
      const msg = caseOpenErrorMessage(err)
      store.setSaveConflict(msg ?? 'Failed to open case. Please try again.')
    }
  }
  // Recent-case row click → quick view.
  const handleOpenRecentCase = async (id: string) => {
    await openCaseQuickView(id)
  }
  // Favourite row click — dispatch by itemType to the matching nav
  // primitive. Flush first (drains pending edits) then navigate. Each
  // branch closes all takeovers so the destination surfaces cleanly.
  // Graceful on a dead target: setCurrentCaseById may throw if the case
  // no longer exists — caught + logged (mirrors handleOpenRecentCase), so
  // clicking a stale favourite never crashes the app.
  const handleOpenFavourite = async (fav: Favourite) => {
    try {
      if (fav.itemType === 'case') {
        // Case favourite → the quick-view modal (same as a Recent row). A
        // case that no longer exists self-heals the dead favourite.
        await openCaseQuickView(fav.itemId, () => {
          void useCasevoStore.getState().removeFavouriteById(
            fav.id,
            "That favourite's case no longer exists — removed it.",
          )
        })
        return
      } else if (fav.itemType === 'task') {
        await navigateTo({ view: 'tasks' }, { intents: (s) => s.setPendingTaskId(fav.itemId) }) // TasksView opens it on mount
      } else if (fav.itemType === 'client') {
        // Honour the stored section (null → default landing). ClientSearchView
        // resolves the client + opens detail.
        await navigateTo({ view: 'clientDetail', clientId: fav.itemId }, { intents: (s) => s.setPendingClientSection(fav.subSection) })
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[Sidebar] Failed to open favourite:', err)
      // Self-heal a DEAD case favourite. setCurrentCaseById throws when
      // the case no longer exists (deleted) — and the CASE path is the
      // ONLY branch that throws (task/client set pending intents that
      // resolve to nothing, so they never reach here). Scope the cleanup
      // to itemType === 'case': remove the stale row and tell the user
      // via the shared favourites toast. New dead favourites are
      // prevented at the source by the delete-handler cascade; this heals
      // any that predate it. Best-effort (removeFavouriteById never
      // throws), so it can't disturb the mobile-overlay close below.
      if (fav.itemType === 'case') {
        void useCasevoStore.getState().removeFavouriteById(
          fav.id,
          "That favourite's case no longer exists — removed it.",
        )
      }
    }
    setSidebarMobileOpen(false)
  }
  // ── FAVOURITES drag-to-reorder (expanded desktop, native HTML5 drag) ──
  // The whole row is draggable, but the drag is HANDLE-GATED via a ref set
  // on the handle's mousedown. We CANNOT gate on onDragStart's e.target:
  // dragstart fires on the drag SOURCE (the draggable <div>), so e.target
  // is the div, not the grabbed grip — closest('[data-fav-drag-handle]')
  // never matched and the drag was always cancelled. mousedown, by
  // contrast, targets the actual element pressed, so the handle's
  // onMouseDown reliably records "the drag began on the grip". A drag
  // started on the label or the star leaves the ref false → cancelled →
  // those keep their plain click behaviour.
  const handleFavDragStart = (e: React.DragEvent<HTMLDivElement>, index: number, fav: Favourite): void => {
    if (!dragFromHandleRef.current) { e.preventDefault(); return }
    setFavDragIndex(index)
    e.dataTransfer.effectAllowed = 'move'
    // setData is required for Firefox to actually start the drag.
    try { e.dataTransfer.setData('text/plain', fav.id) } catch { /* no-op */ }
  }
  const handleFavDragOver = (e: React.DragEvent<HTMLDivElement>, index: number): void => {
    if (favDragIndex === null) return // not our drag
    e.preventDefault() // allow drop
    e.dataTransfer.dropEffect = 'move'
    if (favDragOverIndex !== index) setFavDragOverIndex(index)
  }
  const handleFavDrop = (e: React.DragEvent<HTMLDivElement>, index: number): void => {
    e.preventDefault()
    const from = favDragIndex
    setFavDragIndex(null)
    setFavDragOverIndex(null)
    if (from === null || from === index || !favourites) return
    const ids = favourites.map((f) => f.id)
    const [moved] = ids.splice(from, 1)
    ids.splice(index, 0, moved)
    void useCasevoStore.getState().reorderFavourites(ids)
  }
  const handleFavDragEnd = (): void => {
    dragFromHandleRef.current = false
    setFavDragIndex(null)
    setFavDragOverIndex(null)
  }
  // ── COLLAPSED-RAIL FLYOUT ROWS (option A) ─────────────────
  // Same data + click handlers as the expanded rows. A case favourite's live
  // label is "name · ref" (favLabel) — split for the two-line row.
  const favouriteFlyoutItems: FlyoutItem[] | null = favourites === null ? null : favourites.map((fav) => {
    const label = favLabel(fav)
    const cut = fav.itemType === 'case' ? label.lastIndexOf(' · ') : -1
    const name = cut > 0 ? label.slice(0, cut) : label
    const ref = cut > 0 ? label.slice(cut + 3) : ''
    return {
      key: fav.id,
      kind: fav.itemType,
      name,
      sub: ref ? `${FAVOURITE_TYPE_LABELS[fav.itemType]} · ${ref}` : FAVOURITE_TYPE_LABELS[fav.itemType],
      onOpen: () => { void handleOpenFavourite(fav) },
    }
  })
  const recentFlyoutItems: FlyoutItem[] | null = recentCases === null ? null : recentCases.map((c) => ({
    key: c.id,
    kind: 'case',
    name: getRecentCaseDisplayName(c),
    sub: `${caseRef(c)} · ${compactRelativeTime(c.myLastViewedAt ?? c.updatedAt)}`,
    onOpen: () => { void handleOpenRecentCase(c.id) },
  }))
  // Account-section "Admin" handler. Admin-only navigation —
  // surfaces the Admin page (v1: audit log viewer; future: user
  // invites, deactivation, firm dashboard). Same flush-then-
  // navigate-with-mutual-exclusion shape as handleSettings, with
  // setShowAdmin(true) at the end instead of setShowSettings.
  // The nav item that calls this is itself gated on
  // authUser?.role === 'admin', so defensively the handler can
  // assume an admin caller; the AdminPage component has a
  // render-time gate as belt-and-braces.
  const handleAdmin = async () => {
    if (!(await navigateTo({ view: 'admin' }))) return
    setSidebarMobileOpen(false)
  }
  // Shared row styling — used by GENERAL nav placeholders AND the
  // account section items so all single-line rows align identically
  // in rail/expanded. In rail mode (collapsed && desktop) the row is
  // icon-only and centred; otherwise the icon + label sit at the left
  // with a gap. Mobile is always expanded.
  const rowClass = (destructive = false, active = false) => {
    const base = `flex items-center transition-colors text-sm
                  focus:outline-none focus-visible:border-[rgba(255,255,255,0.35)]`
    // Active = Option B raised card (see ACTIVE_CARD) + gold icon; the old
    // gold wash + 2px left border is gone. Inactive / destructive unchanged.
    const colour = destructive
      ? `${ROW_CARD} py-[5px] text-red-300 hover:bg-red-500/[0.14] hover:border-red-500/20 ${ROW_PRESS}`
      : active
        ? `${ACTIVE_CARD} ${ACTIVE_BAR_ROW} ${sidebarCollapsed ? 'md:before:top-[8px] md:before:bottom-[8px]' : ''} py-[5px] text-white font-semibold [&>svg]:text-[#e0b64a]`
        : `${ROW_CARD} py-[5px] text-casevo-light hover:text-white ${ROW_HOVER} ${ROW_PRESS} focus-visible:bg-[rgba(255,255,255,0.04)]`
    // Rail rows keep the same natural 36px height as expanded rows (my-[2px] +
    // 1px border + py-[5px] + 20px icon), so nav/account icons never move vertically on
    // toggle. The Favourites / Recent flyout icons use the same 36px pitch.
    // Active and inactive now share the card footprint → identical padding
    // (the old asymmetric rail offset existed only because inactive rows were
    // full-width with no border).
    const padding = sidebarCollapsed
      ? 'px-[7px] gap-4 md:px-0 md:gap-0 md:justify-center'
      : 'px-[7px] gap-4'
    return `${base} ${colour} ${padding}`
  }
  // Label visibility — hidden in rail mode on desktop only. On mobile
  // we always show the label because the mobile overlay is full-width.
  // whitespace-nowrap is load-bearing: during the 150ms width-collapse
  // animation the sidebar is transiently narrow, and multi-word labels
  // ("Client Search", "Document Search", "Sign Out") without nowrap WRAP
  // to extra lines at those intermediate widths, then unwrap as the width
  // grows — the changing row heights push everything below up/down (the
  // whole-sidebar "text shifts up then settles"). nowrap keeps every row
  // a fixed single-line height throughout; the nav clips the horizontal
  // overflow so the text doesn't spill mid-animation.
  const labelClass = `whitespace-nowrap ${sidebarCollapsed ? 'md:hidden' : ''}`
  // Section header — text label in expanded mode, thin horizontal rule
  // in rail mode (desktop-only) so the visual grouping is preserved
  // without text fighting for space. Used for both GENERAL and RECENT CASES.
  // The text variant is hidden by `md:hidden` only when collapsed AND
  // on desktop; mobile (no md: applied) shows it regardless.
  // EQUAL-HEIGHT SLOT: both variants occupy the same fixed h-10 (40px) so
  // toggling expanded↔collapsed never shifts the icons below a header.
  // Expanded text = pt-4 (16) + text-xs line (16) + pb-2 (8) = 40px, now
  // pinned with h-10; the collapsed rule sits vertically centred in an
  // identical h-10 box (previously my-3 + 1px = 25px, which pulled Client
  // Search / Calendar / Tasks / Document Search and the Favourites star up
  // ~15px on collapse).
  // RAIL DIVIDER (collapsed desktop only) — THE one divider every group uses:
  // the three section rules AND the rule above Settings / Admin / Support. A
  // 40px-wide line (mx-3 inset), casevo-mid, centred in an h-10 slot, so every
  // line has the same 21.5px gap to the row cards either side (19.5 + the
  // cards' 2px margin) and rows keep their 36px pitch. h-10 matches the
  // expanded text header, so icons don't move on collapse/expand. On SHORT
  // windows (< 740px tall) ALL four slots shrink together to h-6 (13.5px
  // gaps) so the whole rail still fits without squashing — still even.
  const railRule = (
    <div className="hidden md:flex h-10 [@media(max-height:739px)]:h-6 items-center mx-3 shrink-0" aria-hidden="true">
      <div className="w-full border-t border-casevo-mid" />
    </div>
  )
  const sectionHeader = (label: string) => (
    <>
      <div
        className={`h-10 text-xs uppercase tracking-widest text-casevo-mid px-4 pt-4 pb-2 whitespace-nowrap ${sidebarCollapsed ? 'md:hidden' : ''}`}
      >
        {label}
      </div>
      {sidebarCollapsed && railRule}
    </>
  )
  // Cases button is "active" when CaseListScreen is what's actually
  // rendering — i.e. no takeover is mounted AND no case is selected.
  // App.tsx's renderMain precedence makes any takeover win over
  // ViewingPane, so all 5 flags must be false. ViewingPane returns
  // CaseListScreen when currentCase is null.
  // Stage B: added !showDashboard — Dashboard being active must
  // un-glow the Cases button (Cases active-state = "we're on the
  // fallthrough ViewingPane and no case is open"; Dashboard is a
  // separate takeover in front of that fallthrough).
  const isCasesActive = !currentCaseId && !showDashboard && !showSettings && !showAdmin && !showClientSearch && !showCalendar && !showDocumentSearch && !showBilling && !showTasks
  return (
    <>
      {/* Mobile backdrop — sits below the sidebar (z-40 vs z-50) so
          taps on the sidebar still register on the sidebar. */}
      {sidebarMobileOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/30 z-40"
          aria-hidden="true"
          onClick={() => setSidebarMobileOpen(false)}
        />
      )}
      <aside
        aria-label="Sidebar"
        className={`
          sidebar-shell bg-casevo-header text-casevo-light flex flex-col
          fixed inset-y-0 left-0 z-50 w-[240px]
          transform transition-transform duration-200 ease-out
          ${sidebarMobileOpen ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0 md:sticky md:top-0 md:inset-auto md:z-30 md:h-full
          md:transition-[width] md:duration-150
          ${sidebarCollapsed ? 'md:w-[64px]' : 'md:w-[208px]'}
        `}
      >
        {/* Mobile-only drawer top bar — holds the close (X) for the slide-in
            drawer. On DESKTOP the sidebar has NO top bar at all: the primary
            nav starts clean at the very top, directly under the full-width
            topbar, and collapse is driven by the panel icon at the LEFT of
            the topbar (AppHeader) — the old « double-arrow bar is gone. */}
        <div className="md:hidden h-16 flex items-center border-b border-casevo-mid px-3">
          {/* Mobile close X */}
          <button
            type="button"
            onClick={() => setSidebarMobileOpen(false)}
            aria-label="Close menu"
            className="flex items-center justify-center text-casevo-light hover:text-white
                       transition-colors p-2 ml-auto rounded
                       focus:outline-none focus-visible:ring-2 focus-visible:ring-casevo-light"
          >
            <CloseIcon />
          </button>
        </div>
        {/* PRIMARY NAV — Dashboard + Cases + GENERAL zone. PINNED: this
            block is shrink-0 (natural height, never scrolls) so the
            primary destinations stay persistently visible no matter how
            long the FAVOURITES + RECENT CASES lists below get. Only the
            scroll region beneath it scrolls. overflow-x-hidden keeps the
            nowrap labels clipped during the width-collapse animation. */}
        {/* md:pt-2.5 — a small (10px) breathing-room nudge so the top content
            isn't tight to the top edge under the topbar. Not state-dependent,
            so expanded + collapsed both sit at the same nudged position (no
            toggle shift). The flex-1 middle zone absorbs the height, so the
            bottom account group stays pinned. Desktop-only (mobile drawer has
            its own close bar above the nav). */}
        <nav className="shrink-0 overflow-x-hidden md:pt-2.5 flex flex-col" aria-label="Primary navigation">
          {/* Stage B: Dashboard button — top of the nav, above Cases.
              Dashboard is the app's landing view (default post-login
              via showDashboard: true initial). Same rowClass()
              active-branch treatment as every other takeover button. */}
          <RailTooltip label="Dashboard" enabled={sidebarCollapsed}>
          <button
            type="button"
            onClick={() => { void handleDashboard() }}
            aria-label="Dashboard"
            // mt-[14px] = the old mt-3 (12px) + the card's 2px top inset (my-[2px],
            // which this mt overrides) so the first row doesn't move up.
            className={`${rowClass(false, showDashboard)} mt-[14px]`}
          >
            <LayoutDashboardIcon />
            <span className={labelClass}>Dashboard</span>
          </button>
          </RailTooltip>
          {/* Cases button — standalone under Dashboard, outside any
              section heading. Routes to whatever ViewingPane renders
              as its default (CaseListScreen when no case is selected).
              Active-state styling comes from rowClass()'s `active`
              branch — same recipe as the Recent Cases row and the
              General-section buttons. */}
          <RailTooltip label="Cases" enabled={sidebarCollapsed}>
          <button
            type="button"
            onClick={() => { void handleCases() }}
            aria-label="Cases"
            className={rowClass(false, isCasesActive)}
          >
            <FolderIcon />
            <span className={labelClass}>Cases</span>
          </button>
          </RailTooltip>
          {sectionHeader('GENERAL')}
          {NAV_ITEMS_GENERAL.filter(({ label }) => label !== 'Billing' || canUseBilling).map(({ label, Icon }) => {
            // All four general-nav items are now wired.
            const onClick = label === 'Client Search'
              ? () => { void handleClientSearch() }
              : label === 'Calendar'
              ? () => { void handleCalendar() }
              : label === 'My work'
              ? () => { void handleTasks() }
              : label === 'Document Search'
              ? () => { void handleDocumentSearch() }
              : label === 'Billing'
              ? () => { void handleBilling() }
              : undefined
            // Active when this button's takeover flag is true. The
            // takeover flags are 1:1 with the nav items so the label
            // ternary doubles as the discriminator. Reactive selector
            // subscriptions for the flags live up at the top of the
            // component (added when isCasesActive was introduced).
            const isActive = label === 'Client Search' ? showClientSearch
                          : label === 'Calendar' ? showCalendar
                          : label === 'My work' ? showTasks
                          : label === 'Document Search' ? showDocumentSearch
                          : label === 'Billing' ? showBilling
                          : false
            return (
              <RailTooltip key={label} label={label} enabled={sidebarCollapsed}>
              <button
                type="button"
                onClick={onClick}
                aria-label={label}
                className={rowClass(false, isActive)}
              >
                <Icon />
                <span className={labelClass}>{label}</span>
              </button>
              </RailTooltip>
            )
          })}
        </nav>
        {/* SCROLL REGION — only FAVOURITES + RECENT CASES scroll. flex-1
            takes the remaining height between the pinned primary nav above
            and the pinned account block below; overflow-y-auto scrolls the
            two lists internally (long lists no longer push the primary nav
            or the account actions off-screen). overflow-x-hidden preserved
            for the nowrap label clipping. */}
        <div className={`sidebar-scroll flex-1 overflow-y-auto overflow-x-hidden pb-2 flex flex-col ${sidebarCollapsed ? 'md:pb-0' : ''}`}>
          {sectionHeader('FAVOURITES')}
          {/* DESKTOP RAIL — ONE star icon (count badge) → the named flyout. */}
          {sidebarCollapsed && (
            <SidebarFlyout section="favourites" title="Favourites" items={favouriteFlyoutItems}
              emptyText="No favourites yet. Star a case to pin it here." onExpandSidebar={() => setSidebarCollapsed(false)} />
          )}
          {/* Favourites — pinned cases / clients / tasks, already ordered
              by sortOrder from the store. Unlike RECENT CASES (text-only
              rows → whole block md:hidden in the rail), each favourite row
              carries a STAR glyph that stays visible in the collapsed rail
              (icon-only, mirroring the GENERAL nav rows) so the section
              reads as icons rather than a blank gap between dividers. In
              expanded/mobile the star is hidden and the two-line label
              shows exactly as before. Loading / empty text has no icon
              form, so it's md:hidden in the rail (same as RECENT CASES). */}
          {favourites === null ? (
            // ★ NO LAYOUT SHIFT: as many 52px row placeholders as there were
            // favourites last time (remembered per device), not a one-line
            // "Loading..." that the list then pushes RECENT CASES down from.
            <div aria-hidden="true" className={sidebarCollapsed ? 'md:hidden' : ''}>
              {Array.from({ length: rememberedCount(FAV_COUNT_KEY) }, (_, i) => (
                <div key={i} className="h-[52px] px-4 flex flex-col justify-center gap-1.5">
                  <span className={`block h-3 rounded-[6px] bg-white/10 motion-safe:animate-pulse ${sidebarCollapsed ? 'md:w-6 md:mx-auto w-40' : 'w-40'}`} />
                  <span className={`block h-2 w-20 rounded-[6px] bg-white/10 motion-safe:animate-pulse ${sidebarCollapsed ? 'md:hidden' : ''}`} />
                </div>
              ))}
              {rememberedCount(FAV_COUNT_KEY) === 0 && <div className="px-4 py-3 text-xs invisible">No favourites yet</div>}
            </div>
          ) : favourites.length === 0 ? (
            <div className={`px-4 py-3 text-xs text-casevo-mid italic ${sidebarCollapsed ? 'md:hidden' : ''}`}>No favourites yet</div>
          ) : (
            <>

              {/* The labelled favourite rows. Hidden in the desktop rail
                  (md:hidden when collapsed — the rail's star flyout icon
                  above stands in); shown on mobile + desktop-expanded. */}
              <div className={`flex flex-col ${sidebarCollapsed ? 'md:hidden' : ''}`}>
            {favourites.map((fav, index) => {
              // ★ No persistent "active" row: favourites open a MODAL (quick
              // view), not a screen — hover + a brief press only (below).

              // ── EXPANDED ROW — [grip · left] [nav button] [star · right].
              // A relative wrapper (NOT a <button>, so the star button can
              // nest as a sibling). Whole row is draggable but the drag is
              // handle-gated (see handleFavDragStart). The nav button
              // navigates on click; the star removes (stopPropagation). The
              // grip (left) and star (right) are hover-revealed absolute
              // overlays separated by the full label width. This row is
              // hidden in the desktop rail (the parent wrapper md:hidden's
              // it) — the rail shows a single favourites symbol instead.
              const isDragging = favDragIndex === index
              const isDropTarget = favDragOverIndex === index && favDragIndex !== index
              return (
                <div
                  key={fav.id}
                  draggable
                  onDragStart={(e) => handleFavDragStart(e, index, fav)}
                  onDragOver={(e) => handleFavDragOver(e, index)}
                  onDrop={(e) => handleFavDrop(e, index)}
                  onDragEnd={handleFavDragEnd}
                  // Bare positioning wrapper. Hover tint + active accent live
                  // HERE (not the button) so the whole row — including the
                  // right control gutter — highlights consistently. At REST
                  // this is a transparent block; the nav button below is a
                  // byte-for-byte match of the RECENT CASES row.
                  className={`group relative ${ROW_CARD} transition-colors duration-100
                              ${ROW_HOVER} ${ROW_PRESS}
                              ${isDropTarget ? 'ring-2 ring-inset ring-casevo-light' : ''}
                              ${isDragging ? 'opacity-50' : ''}`}
                >
                  {/* Nav button — full-width, identical shape to a RECENT
                      CASES row (flex-col items-start w-full py-2, pl-4 / active
                      pl-[14px] to compensate the container's 2px border). pr-9
                      reserves the right gutter for the hover controls so a long
                      label doesn't slide under them. Row click navigates. */}
                  <button
                    type="button"
                    onClick={() => { void handleOpenFavourite(fav) }}
                    onContextMenu={(e) => { if (fav.itemType === 'case') openFavouriteCaseMenu(e, fav) }}
                    className={`flex flex-col items-start w-full text-left text-casevo-light
                                focus:outline-none focus-visible:bg-[rgba(255,255,255,0.04)] rounded-[9px]
                                py-[5px] pl-[7px] pr-9`}
                  >
                    <span className="text-sm text-casevo-light group-hover:text-white transition-colors truncate w-full">
                      {favLabel(fav)}
                    </span>
                    <span className="text-xs text-casevo-mid uppercase tracking-wider truncate w-full">
                      {FAVOURITE_TYPE_LABELS[fav.itemType]}
                    </span>
                  </button>
                  {/* LEFT overlay — drag GRIP only. Hover-revealed, absolute
                      (out of flow → no resting shift). Sits in the label's
                      left padding gutter (pl-4) so it never covers the text.
                      Separated from the star by the whole label width, so the
                      two controls can't be mis-clicked for each other. */}
                  <div
                    // In the card's 8px left margin gutter (left -9 = margin + border) so
                    // it never overlaps the inset label.
                    className="absolute inset-y-0 left-[-9px] flex items-center opacity-0 pointer-events-none transition-opacity
                               group-hover:opacity-100 group-hover:pointer-events-auto
                               group-focus-within:opacity-100 group-focus-within:pointer-events-auto"
                  >
                    {/* Six-dot grip. onMouseDown records the handle as the drag
                        origin (dragstart's e.target is the draggable div, not
                        the grip, so we can't detect it there). */}
                    <span
                      data-fav-drag-handle
                      onMouseDown={() => { dragFromHandleRef.current = true }}
                      aria-hidden="true"
                      title="Drag to reorder"
                      className="flex items-center pl-0.5 shrink-0 text-casevo-mid hover:text-casevo-light cursor-grab active:cursor-grabbing"
                    >
                      <svg width="10" height="16" viewBox="0 0 10 16" fill="currentColor">
                        <circle cx="2.5" cy="3" r="1.3" /><circle cx="7.5" cy="3" r="1.3" />
                        <circle cx="2.5" cy="8" r="1.3" /><circle cx="7.5" cy="8" r="1.3" />
                        <circle cx="2.5" cy="13" r="1.3" /><circle cx="7.5" cy="13" r="1.3" />
                      </svg>
                    </span>
                  </div>
                  {/* RIGHT overlay — remove STAR only. Hover-revealed, absolute
                      in the pr-9 right gutter. */}
                  <div
                    className="absolute inset-y-0 right-1 flex items-center opacity-0 pointer-events-none transition-opacity
                               group-hover:opacity-100 group-hover:pointer-events-auto
                               group-focus-within:opacity-100 group-focus-within:pointer-events-auto"
                  >
                    {/* Filled (item IS favourited); click → toggleFavourite
                        removes it (stopPropagation, so no nav). */}
                    <FavouriteStar
                      itemType={fav.itemType}
                      itemId={fav.itemId}
                      label={favLabel(fav)}
                      subSection={fav.subSection}
                      variant="dark"
                      className="shrink-0"
                    />
                  </div>
                </div>
              )
            })}
              </div>
            </>
          )}
          {sectionHeader('RECENT CASES')}
          {/* DESKTOP RAIL — ONE history-clock icon (count badge) → the named flyout. */}
          {sidebarCollapsed && (
            <SidebarFlyout section="recent" title="Recent cases" items={recentFlyoutItems}
              emptyText="No recent cases yet." onExpandSidebar={() => setSidebarCollapsed(false)} />
          )}
          {/* Recent Cases content — hidden entirely in desktop rail
              mode (the section divider above remains as the visual
              cue). Mobile + desktop-expanded show Loading / Empty /
              list. The whole content block carries `md:hidden` when
              collapsed so it disappears on desktop only. */}
          <div className={`flex flex-col ${sidebarCollapsed ? 'md:hidden' : ''}`}>
            {recentCases === null ? (
              <div aria-hidden="true">
                {/* Same box as a real row (ROW_CARD + its padding + the two text lines). */}
                {Array.from({ length: rememberedCount(RECENT_COUNT_KEY) }, (_, i) => (
                  <div key={i} className={`${ROW_CARD} py-[5px] px-[7px] flex flex-col`}>
                    <span className="block text-sm"><span className="inline-block align-middle h-3 w-40 rounded-[6px] bg-white/10 motion-safe:animate-pulse" /></span>
                    <span className="block text-xs"><span className="inline-block align-middle h-2 w-24 rounded-[6px] bg-white/10 motion-safe:animate-pulse" /></span>
                  </div>
                ))}
                {rememberedCount(RECENT_COUNT_KEY) === 0 && <div className="px-4 py-3 text-xs invisible">No recent cases</div>}
              </div>
            ) : recentCases.length === 0 ? (
              <div className="px-4 py-3 text-xs text-casevo-mid italic">No recent cases</div>
            ) : (
              recentCases.map((c) => {
                const displayName = getRecentCaseDisplayName(c)
                const reference = caseRef(c) // THE reference (SURNAME-YEAR-ID)
                // Active row gets a subtle tint + 2px left border in
                // the brand light colour. pl-[14px] (instead of px-4)
                // accounts for the 2px border so the text doesn't
                // shift left when a row becomes active.
                // ★ No persistent "active" row: a recent case opens a MODAL
                // (quick view), not a screen — hover + a brief press only.
                const rowPadding = `group ${ROW_CARD} py-[5px] px-[7px] ${ROW_HOVER} ${ROW_PRESS} ${ROW_FOCUS} duration-100`
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => { void handleOpenRecentCase(c.id) }}
                    onContextMenu={(e) => openRecentCaseMenu(e, c)}
                    className={`flex flex-col items-start text-left transition-colors
                                focus:outline-none
                                ${rowPadding}`}
                  >
                    <span className="text-sm text-casevo-light group-hover:text-white transition-colors truncate w-full">
                      {displayName}
                    </span>
                    {/* truncate w-full (like the name line above + the
                        favourite rows) so this "REF · time" line never wraps
                        to a 2nd line at the intermediate narrow widths during
                        the 150ms width-collapse animation — that transient
                        wrap grew row 1's height and shoved row 2 around (the
                        "line briefly appears between the rows"). */}
                    <span className="text-xs text-casevo-mid truncate w-full">
                      {reference} · {compactRelativeTime(c.myLastViewedAt ?? c.updatedAt)}
                    </span>
                  </button>
                )
              })
            )}
          </div>
        </div>
        {/* Account section — separated from nav by a single-pixel rule
            in the same colour as the top bar's border for visual
            symmetry. Settings + Sign Out are functional from day one. */}
        {/* In the desktop rail the full-width border is swapped for the SAME
            inset rule as the section dividers (even gaps); expanded and
            mobile keep the full-width border exactly as before. */}
        <div className={`border-t border-casevo-mid py-2 flex flex-col ${sidebarCollapsed ? 'md:border-t-0 md:pt-0' : ''}`}>
          {sidebarCollapsed && railRule}
          <RailTooltip label="Settings" enabled={sidebarCollapsed}>
          <button
            type="button"
            onClick={() => { void handleSettings() }}
            aria-label="Settings"
            className={rowClass(false, showSettings)}
          >
            <SettingsIcon />
            <span className={labelClass}>Settings</span>
          </button>
          </RailTooltip>
          {/* Admin nav item — admin-only. Gated on
              authUser?.role === 'admin' so non-admins never see
              the row. AdminPage itself has a render-time gate as
              belt-and-braces. Active-state mirrors the other
              takeover items via showAdmin (which is true iff the
              Admin page is the current view). */}
          {authUser?.role === 'admin' && (
            <RailTooltip label="Admin" enabled={sidebarCollapsed}>
            <button
              type="button"
              onClick={() => { void handleAdmin() }}
              aria-label="Admin"
              className={rowClass(false, showAdmin)}
            >
              <ShieldIcon />
              <span className={labelClass}>Admin</span>
            </button>
            </RailTooltip>
          )}
          {/* Support — opens the in-app support modal (message us
              directly, no mail client). Placed in the account section
              right after Settings/Admin and before Sign Out so the
              destructive Sign Out stays last. Not a store takeover:
              it's a self-contained overlay, so it just flips local
              state (and dismisses the mobile overlay so the modal
              isn't stacked behind it). Non-active styling always —
              there's no "current view" for a modal to highlight. */}
          <RailTooltip label="Support" enabled={sidebarCollapsed}>
          <button
            type="button"
            onClick={() => { setSidebarMobileOpen(false); setShowSupport(true) }}
            aria-label="Support"
            className={rowClass(false)}
          >
            <MessageCircleIcon />
            <span className={labelClass}>Support</span>
          </button>
          </RailTooltip>
          <RailTooltip label="Sign Out" enabled={sidebarCollapsed}>
          <button
            type="button"
            onClick={() => setShowSignOutConfirm(true)}
            aria-label="Sign Out"
            className={rowClass(true)}
          >
            <LogOutIcon />
            <span className={labelClass}>Sign Out</span>
          </button>
          </RailTooltip>
        </div>
      </aside>
      {/* Sign Out confirmation — rendered outside the <aside> so its
          fixed positioning isn't trapped by the sidebar's transform
          context. */}
      <ConfirmDialogue
        isOpen={showSignOutConfirm}
        title="Sign out of Casevo?"
        message="You'll need to sign back in next time you visit."
        confirmLabel="Sign Out"
        onConfirm={() => useCasevoStore.getState().clearAuth()}
        onCancel={() => setShowSignOutConfirm(false)}
      />
      {/* Support modal — rendered outside the <aside> so its fixed
          positioning isn't trapped by the sidebar's transform context
          (same reasoning as ConfirmDialogue above). */}
      <SupportModal
        isOpen={showSupport}
        onClose={() => setShowSupport(false)}
      />
      {/* Right-click case actions (Recent + Favourite case rows) — the same
          full menu + dialogs the case list uses. */}
      <CaseActionsMenu menu={caseMenu} onClose={() => setCaseMenu(null)} />
      {/* Recent / favourite case click → the shared case quick-view modal
          (outside the <aside> so its fixed overlay isn't trapped by the
          sidebar's transform context). */}
      {sidebarQuickView && (
        <CaseQuickViewModal
          caseSummary={sidebarQuickView.summary}
          initialFull={sidebarQuickView.full}
          onClose={() => setSidebarQuickView(null)}
          // "Full case →" → the full case page, reusing the fetched row.
          onOpenFull={(id, full) => {
            // The modal passes null after an in-modal edit (its row is stale) —
            // never fall back to the pre-edit row then; refetch instead.
            const prefetched = full ?? undefined
            setSidebarQuickView(null)
            void openFullCase(id, prefetched)
          }}
          // Client link → that client's page (same path as a client favourite).
          onOpenClient={(cs) => {
            setSidebarQuickView(null)
            const clientId = cs.client?.id
            if (!clientId) return
            void navigateTo({ view: 'clientDetail', clientId })
          }}
          // In-modal edits (name / status) → keep the sidebar's Recent rows
          // (and so the live favourite labels) current.
          onPatched={() => { void useCasevoStore.getState().loadRecentCases() }}
        />
      )}
    </>
  )
}
export default Sidebar
